/**
 * API Service Layer
 *
 * This module contains all API calls to the Datastage backend.
 * It provides a centralized location for all HTTP requests.
 *
 * Architecture:
 * - Uses Axios for HTTP requests
 * - All requests include JWT token in Authorization header
 * - Automatic 401 handling redirects to login
 * - Standardized error handling across all endpoints
 *
 * API Base URL: Configured in src/datastage/config/constants.js
 *
 * Usage:
 * ```
 * import api from 'datastage/api';
 *
 * // Fetch data
 * const products = await api.products(filter, range, sort);
 *
 * // Create/Update
 * await api.addProducts(productData);
 * ```
 *
 * Available API Methods:
 * - products()              - Get products list
 * - addProducts()           - Create products
 * - jobs()                  - Get jobs list
 * - dateMath()              - Get date math entries
 * - rights()                - Get rights data
 * - users()                 - Get users list
 * - (see exports at bottom of file for complete list)
 *
 * Error Handling:
 * - 401 errors trigger automatic logout
 * - Other errors are logged and returned to caller
 *
 * @see src/datastage/config/constants.js - API_BASE configuration
 * @see src/datastage/utils/localStorageService.js - Token management
 */
import axios from 'axios';
import { logout } from './store/auth';
import { API_BASE } from './config/constants';
import { getAuthToken, getCurrentUser } from './utils/localStorageService';
import { saveAs } from 'file-saver';
import { singleExportFileName } from './utils/dealVersionExportNaming.js';

/**
 * Creates an Axios instance for making API requests
 *
 * @param {string} token - The authentication token to be used in the request headers
 * @return {AxiosInstance} - The Axios instance configured with the base URL and headers
 */
export const apiClientV1 = (token) =>
    axios.create({
        baseURL: API_BASE,
        headers: {
            Authorization: `Bearer ${token}`,
            'X-Requested-By': 'spa-admin',
        },
    });


/**
 * Fetches products from the API based on filter, range, and sort parameters
 *
 * @param {Object} filter - The filter criteria to apply to the products
 * @param {Array} range - The range of products to fetch (e.g., [0, 10])
 * @param {Array} sort - The sorting criteria to apply to the products (e.g., ["id", "DESC"])
 * @return {Promise<Array<Object>|undefined>} - A promise that resolves with the fetched products or undefined if an error occurs
 */
async function products(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/products?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching products:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

/**
 * Fetches products with load status READY (Push Ready flow).
 * Same request/response contract as {@link products}; only the path differs.
 *
 * @param {Object} filter - Filter criteria to apply
 * @param {Array} range - Range of rows to fetch (e.g., [0, 19])
 * @param {Array} sort - Sort criteria (e.g., ["productId", "DESC"])
 * @return {Promise<Object|undefined>} - Resolves with API data (typically a Spring Page) or undefined on 401
 */
async function readyProducts(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/products/readyProducts?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching ready products:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function pushReadyProducts() {
    try {
        const { data } = await apiClientV1(getAuthToken()).post(`/products/rightsLine/push`, {
            actedOnBy: getCurrentUser(),
        });
        return data;
    } catch (error) {
        console.error('Error pushing ready products:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function rightsLineProductUpdates(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/sourceReports/rightsLineProductUpdates?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching product updates:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return null;
        }

        // Backend returns { message, code } for all errors
        return error?.response?.data || {
            code: 500,
            message: 'Network error - unable to reach server'
        };
    }
}

async function addProducts(products) {
    try {
        let ids, actedOnBy;
        if (Array.isArray(products)) {
            ids = products.map(p => p.id);
            actedOnBy = products[0]?.actedOnBy || getCurrentUser();
        } else {
            ids = [products.id];
            actedOnBy = products.actedOnBy || getCurrentUser();
        }

        console.log('Adding products with IDs:', ids, 'Acted on by:', actedOnBy);
        const { data } = await apiClientV1(getAuthToken()).put(
            `/sourceReports/rightsLine/productUpdate/${ids.length}`,
            {
                actedOnBy,
                action: 'ADD',
                sids: ids,
            },
        );
        console.log('Add products response data:', data);
        return data;
    } catch (error) {
        console.error('Error adding product(s):', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return null;
        }

        // Backend returns { message, code } for all errors
        return error?.response?.data || {
            code: 500,
            message: 'Network error - unable to reach server'
        };
    }
}

async function updateProducts(products) {
    try {
        let ids, actedOnBy;
        if (Array.isArray(products)) {
            ids = products.map(p => p.id);
            actedOnBy = products[0]?.actedOnBy || getCurrentUser();
        } else {
            ids = [products.id];
            actedOnBy = products.actedOnBy || getCurrentUser();
        }
        const { data } = await apiClientV1(getAuthToken()).put(
            `/sourceReports/rightsLine/productUpdate/${ids.length}`,
            {
                actedOnBy,
                action: 'UPDATE',
                sids: ids,
            },
        );
        return data;
    } catch (error) {
        console.error('Error updating product(s):', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return null;
        }

        // Backend returns { message, code } for all errors
        return error?.response?.data || {
            code: 500,
            message: 'Network error - unable to reach server'
        };
    }
}

async function ignoreProducts(products) {
    try {
        let ids, actedOnBy;
        if (Array.isArray(products)) {
            ids = products.map(p => p.id);
            actedOnBy = products[0]?.actedOnBy || getCurrentUser();
        } else {
            ids = [products.id];
            actedOnBy = products.actedOnBy || getCurrentUser();
        }
        const { data } = await apiClientV1(getAuthToken()).put(
            `/sourceReports/radarUpdate/${ids.length}`,
            {
                actedOnBy,
                ignoreFlag: true,
                sids: ids,
            },
        );
        return data;
    } catch (error) {
        console.error('Error ignoring product(s):', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return null;
        }

        // Backend returns { message, code } for all errors
        return error?.response?.data || {
            code: 500,
            message: 'Network error - unable to reach server'
        };
    }
}

async function unIgnoreProducts(products) {
    try {
        let ids, actedOnBy;
        if (Array.isArray(products)) {
            ids = products.map(p => p.id);
            actedOnBy = products[0]?.actedOnBy || getCurrentUser();
        } else {
            ids = [products.id];
            actedOnBy = products.actedOnBy || getCurrentUser();
        }
        const { data } = await apiClientV1(getAuthToken()).put(
            `/sourceReports/radarUpdate/${ids.length}`,
            {
                actedOnBy,
                ignoreFlag: false,
                sids: ids,
            },
        );
        return data;
    } catch (error) {
        console.error('Error unignoring product(s):', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return null;
        }

        // Backend returns { message, code } for all errors
        return error?.response?.data || {
            code: 500,
            message: 'Network error - unable to reach server'
        };
    }
}

async function moveProducts(products, espnTitle) {
    try {
        let ids, actedOnBy;
        if (Array.isArray(products)) {
            ids = products.map(p => p.id);
            actedOnBy = products[0]?.actedOnBy || getCurrentUser();
        } else {
            ids = [products.id];
            actedOnBy = products.actedOnBy || getCurrentUser();
        }
        const status = espnTitle ? 'DISABLED' : 'ENABLED';
        const { data } = await apiClientV1(getAuthToken()).put(
            `/products/rightsLine/handlingEspnTitle/${ids.length}`,
            {
                actedOnBy,
                ids,
                status,
            },
        );
        return data;
    } catch (error) {
        console.error('Error moving product(s):', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return null;
        }

        // Backend returns { message, code } for all errors
        return error?.response?.data || {
            code: 500,
            message: 'Network error - unable to reach server'
        };
    }
}

async function rights(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/rightset/list?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching rights:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function dateMathJobs(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/datemath/jobs?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching date math jobs:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function dateMathCalculations(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/datemath/calculations?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching date math calculations:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function deleteDateMathCalculation(id) {
    try {
        const response = await apiClientV1(getAuthToken()).delete(`/datemath/calculations/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting date match calculation:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function uploadDateMathJob(formData) {
    try {
        const response = await apiClientV1(getAuthToken()).post(`/datemath/jobs`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading date math job:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function createMacro(dateMathMacros) {
    try {
        const { data } = await apiClientV1(getAuthToken()).post(`/datemath/macros`, dateMathMacros);
        return data;
    } catch (error) {
        console.error('Error creating macro:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function jobs(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/jobs?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching jobs:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function jobMetrics(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/jobs/metrics?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching job metrics:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function jobTasks(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/jobs/tasks?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching job tasks:', error);

        if (error?.status === 401) {
            handleUnauthorizedRequest();
        }

        return error?.response?.data;
    }
}

async function jobById(jobId) {
    try {
        return await apiClientV1(getAuthToken()).get(`/jobs/${jobId}`);
    } catch (error) {
        console.error(`Error fetching job status for job ID ${jobId}:`, error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function dealVersions(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/deal/version/list?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching deal versions:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function deleteDealSnapshot(dealId, version, subversion) {
    try {
        const { data } = await apiClientV1(getAuthToken()).delete(
            `/deal/version/snapshot/${dealId}/${version}/${subversion}`,
        );
        return data;
    } catch (error) {
        console.error('Error deleting deal snapshot:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function updateDealStatus(dealToUpdate) {
    try {
        const { data } = await apiClientV1(getAuthToken()).post(
            `deal/version/updateDealStatus`,
            dealToUpdate,
        );
        return data;
    } catch (error) {
        console.error('Error updating deal status:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function checkPDFExists(dealVersion) {
    try {
        const fileName = singleExportFileName(dealVersion, 'pdf');
        const { data } = await apiClientV1(getAuthToken()).get(
            `/deal/version/exists/pdf/${dealVersion.id}/${fileName}`,
        );
        return data;
    } catch (error) {
        console.error('Error downloading deal PDF:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function createPDF(dealVersion) {
    try {
        const fileName = singleExportFileName(dealVersion, 'pdf');
        const { data } = await apiClientV1(getAuthToken()).post(
            `/deal/version/create/pdf/${dealVersion.id}/${fileName}`,
        );
        return data;
    } catch (error) {
        console.error('Error creating pdf:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function downloadPDF(dealVersion) {
    try {
        const fileName = singleExportFileName(dealVersion, 'pdf');
        const url = `/deal/version/pdf/${dealVersion.id}/${fileName}`;
        const response = await apiClientV1(getAuthToken()).get(url, {
            responseType: 'blob',
        });
        saveAs(response.data, fileName);
        return response;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function checkXLSXExists(dealVersion) {
    try {
        const fileName = singleExportFileName(dealVersion, 'xlsx');

        const { data } = await apiClientV1(getAuthToken()).get(
            `/deal/version/exists/excel/${dealVersion.id}/${fileName}`,
        );
        return data;
    } catch (error) {
        console.error('Error downloading XLSX:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function checkComparisonExists(idsToCompare) {
    try {
        const { data } = await apiClientV1(getAuthToken()).post(
            `/deal/version/exists/excelCompare/compare.xlsx`,
            idsToCompare,
        );
        return data;
    } catch (error) {
        console.error('Error downloading XLSX:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function createDealComparison(idsToCompare, fileName) {
    try {
        const { data } = await apiClientV1(getAuthToken()).post(
            `deal/version/create/excelCompare/${fileName}`,
            idsToCompare,
        );
        return data;
    } catch (error) {
        console.error('Error creating xlsx:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function downloadDealComparison(idsToCompare, fileName) {
    try {
        const response = await apiClientV1(getAuthToken()).post(
            `deal/version/get/excelCompare/${fileName}`,
            idsToCompare,
            {
                responseType: 'blob',
            },
        );
        saveAs(response.data, fileName);
        return response;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

/**
 * Check if a PDF comparison file exists for two deal versions.
 * @param {number} id1 - First deal version ID
 * @param {number} id2 - Second deal version ID
 * @returns {Promise<{exists: boolean, fileName: string}>}
 */
async function checkPdfComparisonExists(id1, id2) {
    try {
        const { data } = await apiClientV1(getAuthToken()).get(
            `/deal/version/exists/pdfCompare/${id1}/${id2}/compare.pdf`,
        );
        return data;
    } catch (error) {
        console.error('Error checking PDF comparison exists:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

/**
 * Create a PDF comparison for two deal versions.
 * @param {number} id1 - First deal version ID
 * @param {number} id2 - Second deal version ID
 * @param {string} fileName - The file name for the comparison
 * @returns {Promise<{id: number}>} - Job ID for tracking
 */
async function createPdfComparison(id1, id2, fileName) {
    try {
        const { data } = await apiClientV1(getAuthToken()).post(
            `deal/version/create/pdfCompare/${id1}/${id2}/${fileName}`,
        );
        return data;
    } catch (error) {
        console.error('Error creating PDF comparison:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

/**
 * Download a PDF comparison for two deal versions.
 * @param {number} id1 - First deal version ID
 * @param {number} id2 - Second deal version ID
 * @param {string} fileName - The file name for the comparison
 */
async function downloadPdfComparison(id1, id2, fileName) {
    try {
        const response = await apiClientV1(getAuthToken()).get(
            `deal/version/pdfCompare/${id1}/${id2}/${fileName}`,
            {
                responseType: 'blob',
            },
        );
        saveAs(response.data, fileName);
        return response;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function createXLSX(dealVersion) {
    try {
        const fileName = singleExportFileName(dealVersion, 'xlsx');
        const { data } = await apiClientV1(getAuthToken()).post(
            `/deal/version/create/excel/${dealVersion.id}/${fileName}`,
        );
        return data;
    } catch (error) {
        console.error('Error creating xlsx:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function downloadXLSX(dealVersion) {
    try {
        const fileName = singleExportFileName(dealVersion, 'xlsx');

        const response = await apiClientV1(getAuthToken()).get(
            `/deal/version/excel/${dealVersion.id}/${fileName}`,
            {
                responseType: 'blob',
            },
        );
        saveAs(response.data, fileName);
        return response;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function startJob(data) {
    try {
        const response = await apiClientV1(getAuthToken()).put(
            `/datemath/startDateMathJob/${data?.id}`,
            {
                data: {
                    id: data?.id,
                    actedOnBy: data?.actedOnBy || '',
                },
            },
        );
    } catch (error) {
        console.error('Error starting job:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function deleteJob(data) {
    try {
        const response = await apiClientV1(getAuthToken()).delete(`/datemath/jobs/${data?.id}`, {
            data: {
                sid: data?.id,
                errorCount: data?.errorCount || 0,
                progressCount: data?.progressCount || 0,
                targetCount: data?.targetCount || 0,
            },
        });
    } catch (error) {
        console.error('Error deleting job:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function jobStatuses(isDateMathCalculations) {
    try {
        const params = new URLSearchParams({
            filter: isDateMathCalculations
                ? JSON.stringify({
                      id: ['COMPLETE', 'INVALID', 'FAILED'],
                  })
                : JSON.stringify({}),
            range: `[0,24]`,
            sort: `[id, DESC]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/jobs/jobStatuses`);
        return (data ?? []).map((item, idx) => ({
            id: item.id ?? idx,
            label: item.label ?? item.name ?? item,
        }));
    } catch (error) {
        console.error('Error fetching job statuses:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function jobTypes(isDateMath) {
    try {
        const params = new URLSearchParams({
            filter: isDateMath
                ? JSON.stringify({
                      types: [
                          'LOAD_DATE_MATH_SIMPLE',
                          'LOAD_DATE_MATH_ADVANCED',
                          'LOAD_DATE_MATH_XLSX',
                      ],
                  })
                : JSON.stringify({}),
            range: `[0,24]`,
            sort: `[id, DESC]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/jobs/jobTypes?${params}`);
        return (data ?? []).map((item, idx) => ({
            id: item.id ?? idx,
            label: item.label ?? item.name ?? item,
        }));
    } catch (error) {
        console.error('Error fetching job types:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function deals(dealId) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify({ q: dealId }),
        });

        const { data } = await apiClientV1(getAuthToken()).get(
            `/datemath/searchDealsAPI?${params}`,
        );
        return data;
    } catch (error) {
        console.error('Error fetching rights:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function catalogs(catalogId) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify({ q: catalogId }),
        });

        const { data } = await apiClientV1(getAuthToken()).get(
            `/datemath/searchCatalogItemsAPI?${params}`,
        );
        return data;
    } catch (error) {
        console.error('Error fetching rights:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function fixFailedAssociation(payload) {
    try {
        const { data } = await apiClientV1(getAuthToken()).post(
            `/rightset/fixFailedRelationship`,
            payload,
        );
        return data;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function createAssociation(payload) {
    try {
        const { data } = await apiClientV1(getAuthToken()).post(`/rightset/associate`, payload);
        return data;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function pmxDeliveries(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/delivery/pmx?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching pmx deliveries:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function users(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/users?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching users:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function userRoles() {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify({}),
            range: `[0,24]`,
            sort: `[id, DESC]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/spaUserRoles?${params}`);

        return (Array.isArray(data) ? data : data.items || [])
            .map((item) => ({
                value: item.id,
                label: item.roleName,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    } catch (error) {
        console.error('Error fetching user roles:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function createUser(payload) {
    try {
        const { data } = await apiClientV1(getAuthToken()).post(`/users`, payload);
        return data;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function updateUser(payload) {
    try {
        const { data } = await apiClientV1(getAuthToken()).put(`/users/${payload?.id}`, payload);
        return data;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function deleteUser(id) {
    try {
        const { data } = await apiClientV1(getAuthToken()).delete(`/users/${id}`);
        return data;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function savedTemplates(filter, range, sort) {
    try {

        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(
            `/rltemplate/fields-list?${params}`,
        );


        return data;
    } catch (error) {
        console.error('Error fetching templates:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function rlTemplateActions() {
    try {
        const { data } = await apiClientV1(getAuthToken()).get(`/rltemplate/actions`);

        return (Array.isArray(data) ? data : data.items || []).map((item) => ({
            value: item.id,
            label: item.name,
        }));
    } catch (error) {
        console.error('Error fetching rightsline template actions:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function rlTemplateDateFormats() {
    try {
        const { data } = await apiClientV1(getAuthToken()).get(`/rltemplate/date-formats`);

        return (Array.isArray(data) ? data : data.items || []).map((item) => ({
            value: item.id,
            label: item.name,
        }));
    } catch (error) {
        console.error('Error fetching rightsline template date formats:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function rlTemplateEntityTypes() {
    try {
        const { data } = await apiClientV1(getAuthToken()).get(`/rltemplate/entity-types`);

        return (Array.isArray(data) ? data : data.items || []).map((item) => ({
            value: item.id,
            label: item.name,
        }));
    } catch (error) {
        console.error('Error fetching rightsline template entity types:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function rlTemplatesByType(entityTypeId) {
    try {
        const { data } = await apiClientV1(getAuthToken()).get(
            `rltemplate/entity-types/${entityTypeId}/templates`,
        );

        return (Array.isArray(data) ? data : data.items || []).map((item) => ({
            value: item.id,
            label: item.name,
        }));
    } catch (error) {
        console.error('Error fetching rightsline templates:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function rlTemplateFields(uploadForm) {
    try {
        const { data } = await apiClientV1(getAuthToken()).get(
            `/rltemplate/entity-types/${uploadForm?.entityType.value}/templates/${uploadForm?.template.value}/action-type/${uploadForm?.action.value}/fields`,
        );

        return data;
    } catch (error) {
        console.error('Error fetching template fields:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function rlCustomListSearch(uploadForm) {
    try {
        const { data } = await apiClientV1(getAuthToken()).get(
            `/rltemplate/fields-list/search/${uploadForm?.action.value}/${uploadForm?.entityType.value}/${uploadForm?.template.value}/${uploadForm?.dateFormat.value}`,
        );

        return (Array.isArray(data) ? data : data.items || []).map((item) => ({
            value: item.id,
            label: item.name,
            fieldKeys: item.fieldKeys,
            templateName: item.templateName,
        }));
    } catch (error) {
        console.error('Error fetching template fields:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function saveRLCustomList(payload) {
    try {
        const { data } = await apiClientV1(getAuthToken()).put(
            `/rltemplate/fields-list/${payload.id}`,
            {
                name: payload?.name
            },
        );
        return data;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function createRLCustomList(payload) {
    try {
        const { data } = await apiClientV1(getAuthToken()).post(`/rltemplate/fields-list`, payload);
        return data;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function deleteRLCustomList(customListId) {
    try {
        const { data } = await apiClientV1(getAuthToken()).delete(`/rltemplate/fields-list/${customListId}`, {
            id: customListId,
        });
        return data;
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

async function generateExcelFile(uploadForm, customFields) {
    try {
        const response = await apiClientV1(getAuthToken()).post(
            `/rltemplate/generate`,
            {
                actionType: uploadForm?.action.value,
                entityTypeId: uploadForm?.entityType.value,
                templateId: uploadForm?.template.value,
                dateFormat: uploadForm?.dateFormat.value,
                allFields: uploadForm?.includeAllFields,
                customFields: customFields || [],
            },
            {
                responseType: 'blob',
            }
        );

       const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
        const fileName = extractNameFromHeader(contentDisposition) ?? 'file.xlsx';
        saveAs(response.data, fileName);
    } catch (error) {
        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

function extractNameFromHeader(header) {
    if (!header) return null;
    const match = header.match(/filename="?([^"]+)"?/);
    return match ? match[1] : null;
}


async function dealDROChanges(filter, range, sort) {
    try {
        const params = new URLSearchParams({
            filter: JSON.stringify(filter),
            range: `[${range}]`,
            sort: `[${sort}]`,
        });

        const { data } = await apiClientV1(getAuthToken()).get(`/deal-dro-change/list?${params}`);
        return data;
    } catch (error) {
        console.error('Error fetching deal versions:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

/**
 * Fetches stale titles statistics
 */
async function staleTitlesStats() {
    try {
        const { data } = await apiClientV1(getAuthToken()).get('/sourceReports/stale-titles/stats');
        return data;
    } catch (error) {
        console.error('Error fetching stale titles stats:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        return error?.response?.data || { error: 'No response body' };
    }
}

/**
 * Downloads CSV export of stale titles
 */
async function staleTitlesCsvExport(failureReason = null) {
    try {
        const response = await apiClientV1(getAuthToken()).post(
            '/sourceReports/stale-titles/export',
            { failureReason },
            { responseType: 'blob' }
        );
        return response;
    } catch (error) {
        console.error('Error downloading stale titles CSV:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        throw error;
    }
}

/**
 * Downloads CSV export of stale titles that have Rightsline ID
 */
async function staleTitlesInRlCsvExport(failureReason = null) {
    try {
        const response = await apiClientV1(getAuthToken()).post(
            '/sourceReports/stale-titles/export-rl',
            { failureReason },
            { responseType: 'blob' }
        );
        return response;
    } catch (error) {
        console.error('Error downloading stale titles in RL CSV:', error);

        if (error?.response?.status === 401) {
            handleUnauthorizedRequest();
            return;
        }

        throw error;
    }
}

/**
 * Handles unauthorized requests by logging out the user and redirecting to the login page
 */
const handleUnauthorizedRequest = () => {
    localStorage.removeItem('postLoginRedirect');
    localStorage.removeItem('activePanel');

    logout();
    window.location.replace('/login');
};

// Exporting the API functions for use in other parts of the application
export const datastageApi = {
    products,
    readyProducts,
    pushReadyProducts,
    rights,
    jobs,
    jobMetrics,
    jobTasks,
    jobById,
    dateMathJobs,
    dateMathCalculations,
    deleteDateMathCalculation,
    uploadDateMathJob,
    createMacro,
    jobStatuses,
    jobTypes,
    startJob,
    deleteJob,
    deals,
    catalogs,
    fixFailedAssociation,
    createAssociation,
    dealVersions,
    deleteDealSnapshot,
    updateDealStatus,
    checkPDFExists,
    createPDF,
    downloadPDF,
    checkXLSXExists,
    createXLSX,
    downloadXLSX,
    checkComparisonExists,
    createDealComparison,
    downloadDealComparison,
    checkPdfComparisonExists,
    createPdfComparison,
    downloadPdfComparison,
    pmxDeliveries,
    users,
    userRoles,
    createUser,
    updateUser,
    deleteUser,
    rlTemplateActions,
    rlTemplateDateFormats,
    rlTemplateEntityTypes,
    rlTemplatesByType,
    rlTemplateFields,
    createRLCustomList,
    rlCustomListSearch,
    saveRLCustomList,
    deleteRLCustomList,
    generateExcelFile,
    savedTemplates,
    rightsLineProductUpdates,
    addProducts,
    updateProducts,
    ignoreProducts,
    unIgnoreProducts,
    moveProducts,
    dealDROChanges,
    staleTitlesStats,
    staleTitlesCsvExport,
    staleTitlesInRlCsvExport
};
